import React, { useContext, useState } from 'react'
import moment from 'moment'
import DeleteOutline from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import API from '../../../../services/API'
import AssetUnavailabilityDialog from './AssetUnavailabilityDialog'
import RecordMaintenance from './RecordMaintenance'
import { SectionHeader } from '../../vehicles/VehicleComponents/SectionKit'
import { AssetContext } from './SingleAsset'

// A planned window whose end has passed without maintenance being recorded.
export const isOverdueAssetPeriod = (period) =>
  !!period.type && !period.completedAt && moment().isAfter(period.end)

// Current and upcoming planned downtime for an asset — the asset-side mirror
// of the vehicle UnavailabilitySection. The list lives in SingleAsset state
// (fetched eagerly so the tab badge is accurate on page load); this section
// renders it and hosts the edit/delete/complete dialogs.
const AssetUnavailabilitySection = ({ periods, loading, error, onChanged, onAdd }) => {
  const { asset, loadAsset, reloadMaintenance } = useContext(AssetContext)
  const [editPeriod, setEditPeriod] = useState(null)
  const [editOpen, setEditOpen] = useState(false)
  const [deletePeriod, setDeletePeriod] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState(false)
  const [completePeriod, setCompletePeriod] = useState(null)

  const confirmDelete = async () => {
    if (!deletePeriod) return
    setDeleting(true)
    setDeleteError(false)
    try {
      await API.delete(`/fleet/asset-unavailability/${deletePeriod._id}`)
      setDeletePeriod(null)
      onChanged()
    } catch (e) {
      setDeleteError(true)
    } finally {
      setDeleting(false)
    }
  }

  const statusChip = (period) => {
    if (period.completedAt) return { label: 'Completed', color: 'success' }
    if (isOverdueAssetPeriod(period)) return { label: 'Overdue', color: 'error' }
    if (moment().isAfter(period.start) && moment().isBefore(period.end))
      return { label: 'Current', color: 'error' }
    return { label: 'Upcoming', color: 'default' }
  }

  return (
    <Stack spacing={1.5}>
      <SectionHeader
        title="Planned Downtime"
        caption="Scheduled services, repairs and other windows when this asset is out of use"
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={onAdd}
          >
            Schedule Downtime
          </Button>
        }
      />
      {loading && <LinearProgress />}
      {error && (
        <Alert severity="error">
          Unable to load planned downtime. Please try again later.
        </Alert>
      )}
      {!loading && !error && periods.length === 0 && (
        <Alert severity="success">
          No current or upcoming downtime recorded for this asset.
        </Alert>
      )}
      {!loading && periods.length > 0 && (
        <Stack spacing={1}>
          {periods.map((period) => {
            const status = statusChip(period)
            // The convert target is a maintenance log, which only serviced
            // assets keep — other windows are closed by editing or deleting.
            const canComplete =
              asset?.requiresServicing && !!period.type && !period.completedAt
            return (
              <Stack
                key={period._id}
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  p: 1,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Stack spacing={0.25} minWidth={0}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" fontWeight={700}>
                      {moment(period.start).format('DD/MM/YYYY HH:mm')} –{' '}
                      {moment(period.end).format('DD/MM/YYYY HH:mm')}
                    </Typography>
                    <Chip
                      size="small"
                      label={period.type || 'Unavailable'}
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                    <Chip
                      size="small"
                      label={status.label}
                      color={status.color}
                      sx={{ borderRadius: 1 }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {period.reason || 'No reason given'}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {canComplete && (
                    <Tooltip title="Record the completed work as maintenance">
                      <Button
                        size="small"
                        variant="outlined"
                        color="success"
                        startIcon={<TaskAltIcon />}
                        onClick={() => setCompletePeriod(period)}
                      >
                        Mark Complete
                      </Button>
                    </Tooltip>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditPeriod(period)
                      setEditOpen(true)
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setDeletePeriod(period)}
                  >
                    <DeleteOutline fontSize="small" color="error" />
                  </IconButton>
                </Stack>
              </Stack>
            )
          })}
        </Stack>
      )}

      <AssetUnavailabilityDialog
        open={editOpen}
        assetID={asset?._id}
        period={editPeriod}
        onClose={() => setEditOpen(false)}
        onSaved={onChanged}
      />

      {/* Mark complete: record the work as a maintenance log, prefilled from
          the window — saving also stamps the window as completed server-side. */}
      <RecordMaintenance
        open={!!completePeriod}
        onClose={() => setCompletePeriod(null)}
        onSaved={() => {
          onChanged()
          reloadMaintenance?.()
          loadAsset?.()
        }}
        prefill={
          completePeriod
            ? {
                maintenanceDate: completePeriod.start,
                description: completePeriod.reason || '',
              }
            : undefined
        }
        plannedUnavailabilityId={completePeriod?._id}
      />

      <Dialog open={!!deletePeriod} onClose={() => setDeletePeriod(null)}>
        <DialogTitle sx={{ fontWeight: 800 }}>
          Remove this downtime window?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {deletePeriod &&
              `${moment(deletePeriod.start).format('DD/MM/YYYY HH:mm')} – ${moment(
                deletePeriod.end,
              ).format('DD/MM/YYYY HH:mm')}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The asset will no longer be shown as unavailable during this
            window.
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Unable to remove this window. Please try again later.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletePeriod(null)}
            color="inherit"
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default AssetUnavailabilitySection
